import { PropsWithChildren, useMemo } from 'react';

import { CAST_AI, isOneClick } from '@cast/constants';
import {
  Alert,
  Button,
  WithRef,
  ExternalLink,
  Typography,
  Stack,
} from '@cast/design-system';
import {
  ContinuousRebalancingConfig,
  KentContinuousRebalancingFeatureConfig,
} from '@cast/types-kent';
import { makeValidateSync, getDocsLink } from '@cast/utils';

import { PageFormFooter } from 'components/form-layouts';
import { PageHeader } from 'main-layout/PageHeader';

import { ConsolidationMethodSelection } from './ConsolidationMethodSelection';
import { useKentAbilities } from '../../hooks/useKentAbilities';
import { FIELD_NAMES } from '../_constants';
import { FormValues } from '../_types';
import {
  formValuesToContinuousRebalancingConfig,
  mapContinuousRebalancingConfigToFormValues,
} from '../_utils';
import { AdditionalSettingsSection } from './AdditionalSettingsSection';
import { validationSchema } from '../validation';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { RffForm, RffFormRef, RffSwitch } from '@cast/shared-rff-ui';
import { useConfirmIgnorePodsWithPersistentVolumesSettingDialog } from 'features/cluster/rebalance/hooks/useConfirmIgnorePodsWithPersistentVolumesSettingDialog';

const validate = makeValidateSync(validationSchema);

const Layout = ({
  children,
  testId,
}: PropsWithChildren<{ testId?: string }>) => {
  return (
    <Stack gap={24} data-testid={testId}>
      {children}
    </Stack>
  );
};

const Header = () => {
  return (
    <PageHeader
      p={0}
      heading={isOneClick() ? 'Bin-packing' : 'Consolidation'}
    />
  );
};

type Props = WithRef<
  RffFormRef<FormValues>,
  {
    featureConfig: KentContinuousRebalancingFeatureConfig;
    hasOutdatedKentroller: boolean;
    isPending: boolean;
    onSubmit: (values: ContinuousRebalancingConfig) => void;
  }
>;

export const ConsolidationSettingsForm = ({
  featureConfig,
  hasOutdatedKentroller,
  isPending,
  onSubmit,
  ref,
}: Props) => {
  const confirmLocalPersistentVolumesSettingDialog =
    useConfirmIgnorePodsWithPersistentVolumesSettingDialog();
  const initialValues = useMemo(() => {
    return mapContinuousRebalancingConfigToFormValues({
      config: featureConfig.config,
      hasOutdatedKentroller,
    });
  }, [featureConfig, hasOutdatedKentroller]);

  const { canUpdateFeatures } = useKentAbilities();

  return (
    <RffForm<FormValues>
      ref={ref}
      validate={validate}
      mode={canUpdateFeatures ? 'edit' : 'view'}
      initialValues={initialValues}
      onSubmit={(formValues: FormValues) => {
        const continuousRebalancingConfig =
          formValuesToContinuousRebalancingConfig(formValues);
        const shouldShowPvConfirmationDialog =
          formValues.aggressiveModeEnabled &&
          !!formValues.aggressiveModeConfig?.ignoreLocalPersistentVolumes;
        if (shouldShowPvConfirmationDialog) {
          confirmLocalPersistentVolumesSettingDialog.openDialog({
            onConfirm: () => {
              onSubmit(continuousRebalancingConfig);
            },
          });
        } else {
          onSubmit(continuousRebalancingConfig);
        }
      }}
    >
      {({ form, values }) => (
        <>
          <Layout testId="consolidation-settings-content">
            <Stack direction="row" gap={12} alignItems="center">
              <RffSwitch
                name={FIELD_NAMES.enabled}
                testId="consolidation-settings-enabled-switch"
                size="md"
                disabled={isPending}
              />
              <Header />
            </Stack>
            {hasOutdatedKentroller && (
              <Alert
                testId="consolidation-outdated-kentroller-alert"
                size="sm"
                color="warning"
                title="Kentroller upgrade required"
              >
                <Typography variant="P12M">
                  Current Kentroller version doesn&apos;t support continuous
                  rebalancing. Upgrade to enable new features.
                </Typography>

                <ExternalLink
                  link={getDocsLink('docs_kent_kentroller')}
                  sx={{ typography: 'P12M' }}
                >
                  Documentation
                </ExternalLink>
              </Alert>
            )}
            {!values.enabled && (
              <Alert
                testId="consolidation-karpenter-alert"
                size="sm"
                color="advisory"
                title={`Consolidation is not managed by ${CAST_AI}. Karpenter consolidation may be active for this cluster.`}
              >
                Enable {CAST_AI} to take over with deeper optimization —
                bin-packing, node replacement, and continuous rebalancing that
                goes further than Karpenter.
              </Alert>
            )}
            <ConsolidationMethodSelection
              hasOutdatedKentroller={hasOutdatedKentroller}
            />
            <AdditionalSettingsSection />
          </Layout>
          <PageFormFooter>
            <Button
              testId="consolidation-settings-cancel-button"
              variant="secondary"
              onClick={() => {
                form.restart();
              }}
            >
              Cancel
            </Button>
            <Button
              testId="consolidation-settings-save-button"
              onClick={form.submit}
              loading={isPending}
            >
              Save changes
            </Button>
          </PageFormFooter>
        </>
      )}
    </RffForm>
  );
};

const ConsolidationSettingsContentSkeleton = () => {
  return (
    <Layout testId="consolidation-settings-skeleton">
      <Header />
      <ConsolidationMethodSelection.Skeleton />
      <AdditionalSettingsSection.Skeleton />
    </Layout>
  );
};

type ConsolidationSettingsContentErrorProps = {
  refetch: () => void;
};

const ConsolidationSettingsContentError = ({
  refetch,
}: ConsolidationSettingsContentErrorProps) => {
  return (
    <Layout>
      <Header />
      <FailedToLoad
        title="Failed to load consolidation settings"
        refresh={refetch}
      />
    </Layout>
  );
};

ConsolidationSettingsForm.Skeleton = ConsolidationSettingsContentSkeleton;
ConsolidationSettingsForm.Error = ConsolidationSettingsContentError;
